import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  Sector,
} from "recharts";
import { ICategoryDataSummaryModel } from "../../types/types";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import theme from "../../styles/theme";
import { getColorForId } from "../../utils/chartColors";

interface PieChartWrapperProps {
  category:
    | "type"
    | "source"
    | "reason"
    // | "area"
    | "coAgent"
    | "specialGroups"
    | "specialTags"
    | "zipCode"
    | "county"
    | "customArea";
  yearIds?: number[];
  /** "Under Contracts" is checked: include those Transactions even outside the selected years */
  includeUnderContracts?: boolean;
  mode: "count" | "amount" | "average";
  onSliceClick?: (variableId: number, value: string, color: string) => void;
  yearText?: string | number;
}

export const PieChartWrapper: React.FC<PieChartWrapperProps> = ({
  category,
  yearIds,
  includeUnderContracts = false,
  mode,
  onSliceClick,
  yearText,
}) => {
  const { categories } = useSelector((state: RootState) => state.analytics);
  // What the drop-down has checked: the selected years (the Pipeline folder among them when
  // Pipeline is checked) and, with "Under Contracts", those Transactions from any year folder
  const categoryData = categories[category]?.data?.filter(
    (x) =>
      (includeUnderContracts && x.isUnderContract) ||
      !yearIds?.length ||
      (x.yearId !== undefined && yearIds.includes(x.yearId)),
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // The live theme, which carries affiliate branding; the import above is the product's own.
  const brand = useTheme();

  const data = categoryData
    ?.reduce((acc, item) => {
      const existing = acc.find((i) => i.id === item.id);
      if (existing) {
        existing.totalCount =
          (existing.totalCount || 0) + (item.totalCount || 0);
        existing.totalAmount =
          (existing.totalAmount || 0) + (item.totalAmount || 0);
        existing.sbCount = (existing.sbCount || 0) + (item.sbCount || 0);
        existing.sbAmount = (existing.sbAmount || 0) + (item.sbAmount || 0);
      } else {
        acc.push({
          ...item,
          totalCount: item.totalCount || 0,
          totalAmount: item.totalAmount || 0,
          sbCount: item.sbCount || 0,
          sbAmount: item.sbAmount || 0,
        });
      }
      return acc;
    }, [] as ICategoryDataSummaryModel[])
    // Average Price only accounts for Seller & Buyer transactions
    ?.filter((item) => mode !== "average" || (item.sbCount || 0) > 0)
    ?.map((item) => ({
      name: item.name,
      id: item.id,
      value:
        mode === "count"
          ? item.totalCount || 0
          : mode === "amount"
            ? item.totalAmount || 0
            : (item.sbCount || 0) > 0
              ? (item.sbAmount || 0) / (item.sbCount || 1)
              : 0,
    }))
    ?.sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      }),
    );

  const renderActiveShape = (props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
      props;

    return (
      <g>
        {/* <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
          {payload.name}
        </text> */}
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        {/* <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        /> */}
        {/* <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" /> */}
        {/* <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333"
        >{`PV ${value}`}</text> */}
        {/* <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#999"
        >
          {`(Rate ${(percent * 100).toFixed(2)}%)`}
        </text> */}
      </g>
    );
  };

  return (
    <>
      {yearText && (
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "16px",
            color: brand.palette.primary.dark,
            padding: "5px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            marginBottom: "10px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          Year: {yearText}
        </div>
      )}
      <ResponsiveContainer width="100%" height={450}>
        <PieChart margin={{ top: 20, right: 20, bottom: 60, left: 20 }}>
          <Pie
            data={data}
            cx="50%"
            cy="45%"
            labelLine={true}
            fontSize={isMobile ? 9 : 13}
            label={({ name, percent }) =>
              percent > 0 ? `${name}: ${(percent * 100).toFixed(0)}%` : null
            }
            outerRadius={100}
            paddingAngle={2}
            minAngle={2}
            fill="#8884d8"
            dataKey="value"
            // activeIndex={activeIndex}
            // onMouseEnter={(index) => setActiveIndex(index)}
            activeShape={renderActiveShape}
            onClick={(data, index) =>
              onSliceClick &&
              data.id &&
              onSliceClick(data.id, data.name, getColorForId(data.id))
            }
          >
            {data?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getColorForId(entry.id || index)}
                stroke="#ffffff"
                strokeWidth={0}
                style={{ transition: "all 0.3s ease" }}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [
              mode === "amount" || mode === "average"
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(Number(value))
                : value,
              mode === "count"
                ? "Units"
                : mode === "amount"
                  ? "Volume"
                  : "Average Price (S&B)",
            ]}
          />
          <Legend
            wrapperStyle={{
              paddingTop: "20px",
              fontSize: "12px",
              lineHeight: "18px",
            }}
            layout="horizontal"
            align="center"
            verticalAlign="bottom"
            iconType="circle"
          />
        </PieChart>
      </ResponsiveContainer>
    </>
  );
};
